import { useContext } from "react";
import { SubscriptionStatus } from "../../../OrderFlow/utils/orderFlowUtils";
import { useCompanyConfigQuery } from "../../../SWR/Company/useCompanyConfigQuery";
import setPriceWithCurrency from "../../../Shared/utils/currency";
import { OrderType } from "../../Orders/OrderDetailsAccordion/utils/OrderDetailsAccordion-types";
import SubscriptionContext from "../../store/subscriptionContext";
import { TabValues } from "../../useUpdateOrderDeliveryModal/deliveryModalUtils/const-enums-types";
import { CancelSubscriptionMode, getCurrentSubscriptionStatus } from "../../utils/utils";
import { getCCText, getContactUsToMakeChangesText, getFrequencyText, getOrderType, getOrderTypeDetails, getShowContactUsToMakeChanges, getShowEditButton, getShowPauseButton, getShowResumeButton, getSubUpdatedEffectiveChangeText, getSummaryLocation, } from "../subscriptionsUtils/helperFunctions";
import { useCurrencyLocalizer } from "../../../Shared/hooks/useCurrencyLocalizer";
import { NO_TOTAL_FOUND } from "../../../Shared/components/OrderSummary/orderSummary-utils";
export const useSubscriptionAccordion = (subscription) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    // TODO! This is a temporary fix to get the context to work. The context should be explicitly typed.
    const { subscriptionsStore } = useContext(SubscriptionContext);
    const { orderInfo, subscriptionInfo, orderProgramMeals, subscriptionProducts, displayDiscountedPrices, pauseAllowCancelOrders, cancelAllowCancelOrders, } = subscription;
    const { customText: { subscriptionChangeEffectiveText }, } = subscriptionsStore;
    const { doesEditFlowExist, subscriptionStatus, UsesPausedSubscriptions, UsesEditSubscriptions, UsesSubscriptionCancellableByCustomer, UsesShowCancellationInstructions, orderId, orderStatus, CancelSubscriptionMode: cancelSubMode, dateChangesTakeEffect, subscriptionLinkId, plan, } = subscriptionInfo;
    const { culture } = useCompanyConfigQuery();
    const hideUnsubscribeButton = cancelSubMode === CancelSubscriptionMode.None;
    const customerCreditCard = subscriptionsStore.customerData.billing &&
        subscriptionsStore.customerData.billing.find((card) => card.IsPrimary === true);
    const currentSubscriptionStatus = getCurrentSubscriptionStatus(subscriptionStatus);
    const currentOrderId = orderId;
    const currentOrderStatus = orderStatus;
    const subscriptionDetails = [];
    orderProgramMeals.map((day) => {
        return day.map((programMeal) => {
            let day = subscriptionDetails.find((day) => day.day === programMeal.day);
            if (day) {
                let meal = day.meals.find((meal) => meal.type === programMeal.meal);
                if (!meal) {
                    day.meals.push({ type: programMeal.meal, quantity: programMeal.quantity });
                }
            }
            else {
                subscriptionDetails.push({
                    day: programMeal.day,
                    meals: [{ type: programMeal.meal, quantity: programMeal.quantity }],
                });
            }
        });
    });
    subscriptionProducts &&
        subscriptionProducts.map((product) => {
            let mealDay;
            let mealDayForProductDay = subscriptionDetails.find((day) => day.day === product.DayOfWeekString);
            if (!mealDayForProductDay) {
                subscriptionDetails.push({
                    day: product.DayOfWeekString,
                    meals: [],
                });
            }
            if (product.DayOfWeek === 0) {
                mealDay = subscriptionDetails[0];
            }
            else {
                mealDay = subscriptionDetails.find((day) => day.day === product.DayOfWeekString);
            }
            if (mealDay.products) {
                mealDay.products.push({ name: product.ProductName, quantity: product.Quantity });
            }
            else {
                mealDay.products = [{ name: product.ProductName, quantity: product.Quantity }];
            }
            let dayIndex = subscriptionDetails.findIndex((day) => day.day == mealDay.day);
            subscriptionDetails[dayIndex] = mealDay;
        });
    const showCancelInfoModal = cancelSubMode === CancelSubscriptionMode.ShowCancellationInstructions;
    const subscriptionIsPausedOrActive = subscriptionStatus === SubscriptionStatus.Active || subscriptionStatus === SubscriptionStatus.Paused;
    const daySorter = {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
    };
    subscriptionDetails.sort((a, b) => daySorter[a.day] - daySorter[b.day]);
    const isSubscriptionEditable = UsesPausedSubscriptions ||
        UsesEditSubscriptions ||
        UsesSubscriptionCancellableByCustomer ||
        UsesShowCancellationInstructions;
    const orderType = getOrderType(subscription);
    const orderTypeDetails = getOrderTypeDetails(subscription);
    const locationInfo = getSummaryLocation(orderTypeDetails, orderType);
    // The total from the balance object is the total for display; it includes all relevant discounts and charges.
    const totalPrice = (_d = (_c = (_b = (_a = subscription === null || subscription === void 0 ? void 0 : subscription.orderInfo) === null || _a === void 0 ? void 0 : _a.Balance) === null || _b === void 0 ? void 0 : _b.cost) === null || _c === void 0 ? void 0 : _c.subscription) === null || _d === void 0 ? void 0 : _d.total;
    // If the discountPerMeal setting is true, use the subtotal with line item discount.
    const subtotal = displayDiscountedPrices
        ? (_j = (_h = (_g = (_f = (_e = subscription === null || subscription === void 0 ? void 0 : subscription.orderInfo) === null || _e === void 0 ? void 0 : _e.Balance) === null || _f === void 0 ? void 0 : _f.cost) === null || _g === void 0 ? void 0 : _g.subscription) === null || _h === void 0 ? void 0 : _h.adjustmentBreakdown) === null || _j === void 0 ? void 0 : _j.subtotalWithLineItemDiscount
        : (_o = (_m = (_l = (_k = subscription === null || subscription === void 0 ? void 0 : subscription.orderInfo) === null || _k === void 0 ? void 0 : _k.Balance) === null || _l === void 0 ? void 0 : _l.cost) === null || _m === void 0 ? void 0 : _m.subscription) === null || _o === void 0 ? void 0 : _o.subtotal;
    // Get the localized currency text for the subtotal display.
    const subtotalPriceCurrencyText = useCurrencyLocalizer(subtotal).localizedCurrencyText;
    // ********** Grouped Component Props *********
    /**
     * Accordion Summary
     */
    const summaryProps = {
        currentSubscriptionStatus: currentSubscriptionStatus,
        locationInfo,
        subscriptionNumber: String(subscriptionLinkId),
        subtotal: subtotal,
        subtotalPriceCurrencyText: subtotalPriceCurrencyText !== null && subtotalPriceCurrencyText !== void 0 ? subtotalPriceCurrencyText : NO_TOTAL_FOUND,
        totalPrice: (_s = (_r = (_q = (_p = subscription === null || subscription === void 0 ? void 0 : subscription.orderInfo) === null || _p === void 0 ? void 0 : _p.Balance) === null || _q === void 0 ? void 0 : _q.cost) === null || _r === void 0 ? void 0 : _r.subscription) === null || _s === void 0 ? void 0 : _s.total,
    };
    /**
     * Accordion Details
     */
    const programDetailsProps = {
        dietaryLifeStyleChoice: orderInfo.subscription_recipe_type_name,
        frequency: getFrequencyText(orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.subscription_period),
        locationInfo,
        programName: plan,
        subtotal: subtotal,
        subtotalPriceCurrencyText: subtotalPriceCurrencyText !== null && subtotalPriceCurrencyText !== void 0 ? subtotalPriceCurrencyText : NO_TOTAL_FOUND,
        totalPrice: setPriceWithCurrency(totalPrice, culture),
    };
    /**
     * Accordion Details
     */
    const billingDetailsProps = {
        ccText: getCCText(customerCreditCard),
    };
    /**
     * Accordion Details
     */
    const subscriptionDetailsProps = {
        subscriptionDetails,
    };
    /**
     * Accordion Details
     */
    const actionsButtonProps = {
        initTabProps: {
            initTabValue: orderType === OrderType.PICKUP ? TabValues.PICKUP : TabValues.DELIVERY,
            initPickupLocation: orderType === OrderType.PICKUP ? subscription.orderInfo.subscription_location_sku : undefined,
            initDeliveryLocation: orderType === OrderType.DELIVERY ? subscription.orderInfo.subscription_address_id : undefined,
            // Gift cards do not have a delivery schedule. This does not *currently* matter, because a gift card does not have details content
            // and cannot be updated.
            initOrderDeliveryScheduleID: (_t = subscription.deliverySchedule.deliveryScheduleId) !== null && _t !== void 0 ? _t : -1,
            programSKU: (_u = orderInfo.program_sku) !== null && _u !== void 0 ? _u : "",
        },
        showEditButton: getShowEditButton(UsesEditSubscriptions, subscriptionStatus),
        subUpdatedEffectiveChangeText: getSubUpdatedEffectiveChangeText(dateChangesTakeEffect, isSubscriptionEditable, subscriptionChangeEffectiveText, subscriptionIsPausedOrActive, subscriptionInfo.FirstDeliveryAfterResume, subscriptionInfo.subscriptionStatus, subscriptionInfo.RenewOnResume),
        contactUsToMakeChangesText: getContactUsToMakeChangesText(UsesEditSubscriptions, doesEditFlowExist, subscriptionStatus),
        showContactUsToMakeChanges: getShowContactUsToMakeChanges(UsesEditSubscriptions, doesEditFlowExist, subscriptionStatus),
        showPauseButton: getShowPauseButton(UsesPausedSubscriptions, subscriptionStatus),
        showResumeButton: getShowResumeButton(UsesPausedSubscriptions, subscriptionStatus),
        currentOrderId,
        currentOrderInfo: subscription.orderInfo,
        currentOrderNumber: subscription.orderInfo.order_number,
        currentOrderStatus,
        subscriptionInfo,
        showCancelInfoModal,
        hideUnsubscribeButton,
        subscriptionIsPausedOrActive,
        programId: orderInfo.program_id,
        subscriptionStatus,
        cancelAllowCancelOrders,
        pauseAllowCancelOrders,
    };
    /**
     * Collected Accordion Details Props
     */
    const accordionDetailsProps = {
        actionsButtonProps,
        billingDetailsProps,
        programDetailsProps,
        subscriptionDetailsProps,
    };
    return {
        summaryProps,
        programDetailsProps,
        subscriptionDetailsProps,
        billingDetailsProps,
        actionsButtonProps,
        accordionDetailsProps,
    };
};
